/** OPTION F — ONE Pulse.
 *  A quietly-alive vertical timeline: a single hairline pulse travels down the
 *  lifecycle stages, briefly illuminating the label as it passes.  Real-feel
 *  operational numbers sit alongside — read as executive telemetry, not marketing.
 *  Everything else stays Executive-Minimal calm.  All motion is CSS-only.
 */
import { ArrowRight, Loader2, ChevronLeft, Activity } from "lucide-react";
import { Link } from "react-router-dom";
import { useLoginAuth } from "./useLoginAuth";

const STAGES = [
  { label: "Procured",  detail: "PO to receipt",             metric: "1,284",   unit: "assets in flight" },
  { label: "Deployed",  detail: "Handed to employees",       metric: "12,847",  unit: "under management" },
  { label: "Managed",   detail: "Golden Asset Record",       metric: "68,412",  unit: "records live" },
  { label: "Supported", detail: "Live service tickets",      metric: "34",      unit: "open incidents" },
  { label: "Recovered", detail: "Offboarding pickups",       metric: "62",      unit: "returns this week" },
  { label: "Reused",    detail: "Refurbished or redeployed", metric: "2,431",   unit: "circular value" },
  { label: "Recycled",  detail: "Certified end-of-life",     metric: "486",     unit: "with certificate" },
];

export default function OptionF() {
  const a = useLoginAuth();
  return (
    <div className="min-h-screen bg-[color:var(--st-bg)] text-[color:var(--st-text)] flex flex-col" data-testid="login-option-f">
      <header className="border-b border-[color:var(--st-border)]">
        <div className="max-w-6xl mx-auto flex items-center h-14 px-6 md:px-8">
          <div className="text-[13px] font-semibold tracking-[0.14em]">SUNTRONIX ONE</div>
          <span className="mx-3 text-[color:var(--st-text-hint)]">·</span>
          <span className="text-[11px] uppercase tracking-[0.18em] text-[color:var(--st-text-muted)]">
            Operating Signals
          </span>
          <div className="ml-auto flex items-center gap-4 text-[11px] text-[color:var(--st-text-muted)]">
            <a href="/help/status" className="hover:text-[color:var(--st-text)] inline-flex items-center gap-1">
              <Activity className="h-3 w-3" strokeWidth={1.5} /> System status
            </a>
            <a href="/help" className="hover:text-[color:var(--st-text)]">Help</a>
          </div>
        </div>
      </header>

      <main className="flex-1">
        <div className="max-w-6xl mx-auto grid lg:grid-cols-12 gap-8 lg:gap-16 px-6 md:px-8 pt-10 md:pt-16 pb-14">
          {/* BRAND SIDE — pulse timeline */}
          <div className="lg:col-span-7 flex flex-col justify-between min-h-[70vh]">
            <div>
              <div className="text-[11px] uppercase tracking-[0.18em] text-[color:var(--st-text-muted)]">Signals from your estate</div>
              <h1 className="mt-4 font-heading text-[36px] md:text-[42px] leading-[1.05] tracking-[-0.02em] font-medium max-w-[18ch]">
                A living pulse across every technology lifecycle.
              </h1>
            </div>

            {/* Timeline rail */}
            <div className="mt-10 relative">
              <div className="absolute left-[7px] top-1 bottom-1 w-px bg-[color:var(--st-border-strong)]" aria-hidden="true" />
              <div className="st-pulse-marker" aria-hidden="true" />
              <ul className="space-y-6 relative">
                {STAGES.map((s, i) => (
                  <li key={s.label} className="pl-8 relative st-pulse-row" style={{ "--i": i, "--n": STAGES.length }}>
                    <span className="absolute left-0 top-1.5 h-[15px] w-[15px] rounded-full border border-[color:var(--st-border-strong)] bg-[color:var(--st-bg)] st-pulse-dot" aria-hidden="true" />
                    <div className="flex items-baseline justify-between gap-4">
                      <div>
                        <div className="st-pulse-label text-[13px] font-semibold uppercase tracking-[0.14em] text-[color:var(--st-text)]">{s.label}</div>
                        <div className="text-[12px] text-[color:var(--st-text-muted)] mt-0.5">{s.detail}</div>
                      </div>
                      <div className="text-right">
                        <div className="font-heading font-semibold text-[color:var(--st-text)] text-[20px] tracking-[-0.01em] tabular-nums">{s.metric}</div>
                        <div className="text-[10px] uppercase tracking-[0.14em] text-[color:var(--st-text-subtle)]">{s.unit}</div>
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            </div>

            <div className="mt-10 text-[11px] uppercase tracking-[0.18em] text-[color:var(--st-text-subtle)]">
              Live · figures update in production
            </div>
          </div>

          {/* AUTH SIDE */}
          <div className="lg:col-span-5 lg:pl-8 lg:border-l lg:border-[color:var(--st-border)] flex items-center">
            <div className="w-full max-w-[380px]">
              <div className="text-[10px] uppercase tracking-[0.18em] text-[color:var(--st-text-muted)]">Welcome to ONE</div>
              <h2 className="font-heading text-[22px] font-medium tracking-[-0.01em] mt-1">Access your workspace</h2>

              {a.step === "email" ? (
                <form onSubmit={a.continueEmail} className="mt-8 space-y-4">
                  <Field label="Work email" type="email" value={a.email} onChange={a.setEmail}
                    required autoFocus autoComplete="email" placeholder="name@company.com" testId="f-email" />
                  {a.error && <ErrorMsg msg={a.error} />}
                  <button type="submit" disabled={a.loading || !a.email}
                    className="w-full h-11 rounded-md bg-[color:var(--st-text)] text-white font-medium inline-flex items-center justify-center gap-2 disabled:opacity-50 hover:bg-[#17263C] transition-colors"
                    data-testid="f-continue">
                    {a.loading ? <Loader2 className="h-4 w-4 animate-spin" /> : (<>Continue <ArrowRight className="h-4 w-4" strokeWidth={1.6} /></>)}
                  </button>
                </form>
              ) : (
                <form onSubmit={a.submitPassword} className="mt-8 space-y-4">
                  <button type="button" onClick={a.backToEmail}
                    className="text-[12px] text-[color:var(--st-text-muted)] hover:text-[color:var(--st-text)] inline-flex items-center gap-1">
                    <ChevronLeft className="h-3.5 w-3.5" /> {a.email}
                  </button>
                  <Field label="Password" value={a.password} onChange={a.setPassword} type="password"
                    required autoFocus autoComplete="current-password" testId="f-password"
                    trailing={<Link to="/forgot-password" className="text-[11px] text-[color:var(--st-text-muted)] hover:text-[color:var(--st-text)]">Forgot password?</Link>} />
                  {a.error && <ErrorMsg msg={a.error} />}
                  <button type="submit" disabled={a.loading}
                    className="w-full h-11 rounded-md bg-[color:var(--st-text)] text-white font-medium disabled:opacity-50 inline-flex items-center justify-center gap-2 hover:bg-[#17263C] transition-colors">
                    {a.loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Sign in"}
                  </button>
                </form>
              )}

              <div className="mt-12 text-[10px] uppercase tracking-[0.18em] text-[color:var(--st-text-subtle)]">
                Encrypted · SSO available · ISO 27001
              </div>
            </div>
          </div>
        </div>
      </main>

      <footer className="border-t border-[color:var(--st-border)] py-5">
        <div className="max-w-6xl mx-auto px-6 md:px-8 flex justify-between text-[11px] text-[color:var(--st-text-subtle)]">
          <span>© 2026 Suntronix. All rights reserved.</span>
          <div className="flex gap-6">
            <a href="/help/trust" className="hover:text-[color:var(--st-text)]">Security &amp; Privacy</a>
            <a href="/help/accessibility" className="hover:text-[color:var(--st-text)]">Accessibility</a>
            <a href="/help/status" className="hover:text-[color:var(--st-text)]">System status</a>
            <a href="/help" className="hover:text-[color:var(--st-text)]">Help</a>
          </div>
        </div>
      </footer>
    </div>
  );
}

function Field({ label, value, onChange, trailing, testId, ...p }) {
  return (
    <label className="block">
      <div className="flex items-baseline justify-between">
        <span className="text-[11px] uppercase tracking-[0.14em] text-[color:var(--st-text-muted)]">{label}</span>
        {trailing}
      </div>
      <input {...p} value={value} onChange={(e) => onChange(e.target.value)} data-testid={testId}
        className="mt-2 w-full h-11 px-3 rounded-md border border-[color:var(--st-border)] bg-white text-[15px] text-[color:var(--st-text)] placeholder:text-[color:var(--st-text-hint)] focus:outline-none focus:border-[color:var(--st-text)] focus:ring-2 focus:ring-[color:var(--st-text)]/10" />
    </label>
  );
}
function ErrorMsg({ msg }) {
  return (
    <div role="alert" data-testid="f-error"
      className="text-[12px] text-[color:var(--st-critical)] bg-[color:var(--st-critical-soft)] border border-[color:var(--st-critical-soft)] rounded-md px-3 py-2">
      {msg}
    </div>
  );
}
